import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { CardContent, Grid, Typography } from '@mui/material';

import { useMutation } from '@apollo/client/react';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { Or2Card } from '@/components/Surface/Or2Card';

import type { AssistantFormData } from '../../form/AssistantForm';
import { AssistantSchema, AssistantForm } from '../../form/AssistantForm';

import { ASSISTANT_RECENT_CONVERSATIONS_QUERY } from './welcome/AssistantRecentConversations';
import { AssistantSuggestedQuestions } from './welcome/AssistantSuggestedQuestions';

export const CREATE_CONVERSATION_WITH_PROMPT_MUTATION = graphql(`
  mutation CreateConversationWithPrompt(
    $params: CreateConversationWithPromptInput
  ) {
    createConversationWithPrompt(params: $params) {
      ortooGptConversation {
        _id
      }
    }
  }
`);

const ASSISTANT_WELCOME_CURRENT_USER_FRAGMENT = graphql(`
  fragment assistantWelcomeCurrentUserFields on User {
    _id
    firstName
  }
`);

interface AssistantWelcomeProps {
  currentUser:
    | DocumentType<typeof ASSISTANT_WELCOME_CURRENT_USER_FRAGMENT>
    | null
    | undefined;
  onConversationSelect: (conversationId: string) => void;
  initialPrompt?: string;
  initialFileIds?: string[];
}

export const AssistantWelcome = ({
  currentUser,
  onConversationSelect,
  initialPrompt,
  initialFileIds,
}: AssistantWelcomeProps) => {
  const { firstName } = currentUser ?? {};

  const [createConversationWithPrompt, { loading, error }] = useMutation(
    CREATE_CONVERSATION_WITH_PROMPT_MUTATION,
    {
      refetchQueries: [ASSISTANT_RECENT_CONVERSATIONS_QUERY],
      onCompleted(data) {
        const { createConversationWithPrompt } = data;

        const conversationId =
          createConversationWithPrompt?.ortooGptConversation?._id;

        if (!conversationId) {
          return;
        }

        onConversationSelect(conversationId);
      },
    },
  );

  const { control, getValues, handleSubmit, setValue } = useForm({
    values: {
      prompt: initialPrompt || '',
      experimental: false,
      fileIds: initialFileIds || [],
    },
    resolver: zodResolver(AssistantSchema),
  });

  const onSubmit = (data: AssistantFormData) => {
    createConversationWithPrompt({
      variables: {
        params: {
          prompt: data.prompt,
          experimental: data.experimental,
          fileIds: data.fileIds ?? [],
        },
      },
    });
  };

  return (
    <Or2Card or2Variant="inner">
      <CardContent>
        <Grid container>
          <Grid>
            <Typography
              color="primary"
              variant="h2"
              fontSize={24}
              fontStyle="normal"
              fontWeight={700}
              lineHeight={1.5}
            >
              {firstName ? `Hi, ${firstName}!` : 'Hi!'}
            </Typography>
            <Typography color="textSecondary">
              I&apos;m powered by AI, and I draw on expertise from{' '}
              <b>GovernorHub Knowledge</b> to provide advice and guidance
            </Typography>
          </Grid>

          <Grid width="100%" my={2}>
            <AssistantForm
              textFieldProps={{
                placeholder: 'How can I help?',
              }}
              control={control}
              getValues={getValues}
              setValue={setValue}
              loading={loading}
              errorMessage={error?.message}
              onSend={handleSubmit(onSubmit)}
            />
          </Grid>
          <Grid>
            <AssistantSuggestedQuestions
              loading={loading}
              onQuestionClick={(question) =>
                setValue('prompt', question, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
            />
          </Grid>
        </Grid>
      </CardContent>
    </Or2Card>
  );
};
